// r24 Product Catalog — Apostille A5 product page renderer.
// Composes hero + trust strip + chain visualisation + tiers + accepted-by + process + FAQ + related.
// Consumed by /apostille/products/$slug (TH) and /en/apostille/products/$slug (EN).

import ProductDepthBlock from "@/components/ProductDepthBlock";
import { Link } from "@tanstack/react-router";
import { Check, Clock, ShieldCheck, Globe2, ArrowRight, FileCheck2, Stamp, Send } from "lucide-react";
import type { ApostilleProduct } from "@/data/apostille-products";
import { APOSTILLE_LEAD_ATTORNEY, relatedApostilleProducts, APOSTILLE_TURNAROUND_EXPRESS } from "@/data/apostille-products";
import ApostilleProductCTA from "./ApostilleProductCTA";
import { CrossSellStrip } from "@/components/CrossSellStrip";
import ConsultantAdvisoryBlock from "@/components/ConsultantAdvisoryBlock";

interface Props {
  product: ApostilleProduct;
  locale: "th" | "en";
  faqs: { q: string; a: string }[];
}

export default function ApostilleProductPage({ product, locale, faqs }: Props) {
  const isTh = locale === "th";
  const name = isTh ? product.nameTh : product.nameEn;
  const catLabel = isTh ? product.categoryLabelTh : product.categoryLabelEn;
  const longDesc = isTh ? product.longDescTh : product.longDescEn;
  const bullets = isTh ? product.bulletTh : product.bulletEn;
  const steps = isTh ? product.processStepsTh : product.processStepsEn;
  const accepted = isTh ? product.acceptedByTh : product.acceptedByEn;
  const related = relatedApostilleProducts(product.slug, 6);

  const L = isTh ? {
    trust: `รับรองโดย ${APOSTILLE_LEAD_ATTORNEY} + นักแปลจดทะเบียนกระทรวงยุติธรรม`,
    priceLabel: "ค่าบริการต่อชุด",
    turnLabel: "ระยะเวลา",
    acceptedBy: "ยอมรับโดย",
    usedFor: "ใช้ยื่นได้กับ",
    whatIncluded: "รวมในราคานี้",
    chainTitle: "Chain การรับรอง 4 ขั้น",
    tiers: "ทางเลือกความเร็ว",
    tierStd: `มาตรฐาน — ${product.turnaround}`, tierExp: `เร่งด่วน — ${APOSTILLE_TURNAROUND_EXPRESS}`, tierPre: "Pre-check เอกสาร (ฟรี)",
    tierStdDesc: "ยอดนิยม — เหมาะกับส่วนใหญ่", tierExpDesc: "MFA เร่งด่วน + Apostille ปลายทางแบบ walk-in", tierPreDesc: "ตรวจสแกนก่อนเริ่มขั้นตอน — ไม่มีค่าใช้จ่าย",
    howTitle: "ขั้นตอนการทำ 4 ขั้น",
    faqTitle: "คำถามที่พบบ่อย",
    relatedTitle: "บริการที่เกี่ยวข้อง",
    breadcrumb: ["หน้าหลัก", "Apostille / MFA", "สินค้าและบริการ"],
    directAnswer: `฿${product.priceMin.toLocaleString()}–฿${product.priceMax.toLocaleString()} · ${product.turnaround} · แปล + Notary + MFA + Apostille/สถานทูต ครบในสำนักงานเดียว`,
    validity: `ระยะเวลายอมรับปลายทาง: ${product.doctype.validityWindow}`,
  } : {
    trust: `Certified by ${APOSTILLE_LEAD_ATTORNEY} + MoJ-registered translators`,
    priceLabel: "Fee per set",
    turnLabel: "Turnaround",
    acceptedBy: "Accepted by",
    usedFor: "Used for",
    whatIncluded: "Included in this fee",
    chainTitle: "The 4-step legalisation chain",
    tiers: "Service tiers",
    tierStd: `Standard — ${product.turnaround}`, tierExp: `Express — ${APOSTILLE_TURNAROUND_EXPRESS}`, tierPre: "Pre-flight review (free)",
    tierStdDesc: "Most popular — right for most cases", tierExpDesc: "MFA priority + destination Apostille walk-in", tierPreDesc: "Free scan review before we start — flags red flags before invoicing",
    howTitle: "How to book — 4 steps",
    faqTitle: "Frequently asked questions",
    relatedTitle: "Related services",
    breadcrumb: ["Home", "Apostille / MFA", "Products"],
    directAnswer: `THB ${product.priceMin.toLocaleString()}–${product.priceMax.toLocaleString()} · ${product.turnaround} · Translation + Notary + MFA + destination Apostille in a single office.`,
    validity: `Destination acceptance window: ${product.doctype.validityWindow}`,
  };

  const catalogPath = isTh ? "/apostille/products" : "/en/apostille/products";
  const hubPath = isTh ? "/apostille" : "/en/apostille";

  const chainIcons = [FileCheck2, Stamp, Send, Globe2];

  return (
    <main className="min-h-screen bg-background pb-28 md:pb-16">
      <nav aria-label="Breadcrumb" className="mx-auto max-w-6xl px-4 pt-6 text-xs text-slate-500">
        <ol className="flex flex-wrap gap-1">
          <li><Link to={isTh ? "/" : "/en"} className="hover:text-blue-700">{L.breadcrumb[0]}</Link></li>
          <li>›</li>
          <li><Link to={hubPath} className="hover:text-blue-700">{L.breadcrumb[1]}</Link></li>
          <li>›</li>
          <li><Link to={catalogPath} className="hover:text-blue-700">{L.breadcrumb[2]}</Link></li>
          <li>›</li>
          <li aria-current="page" className="text-slate-700">{name}</li>
        </ol>
      </nav>

      <section className="mx-auto max-w-6xl px-4 pt-6">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_1fr]">
          <div>
            <div className="mb-3 flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-widest">
              <span className="rounded-full bg-blue-100 px-3 py-1 text-blue-800">{catLabel}</span>
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">
                <ShieldCheck className="mr-1 inline h-3 w-3" /> Hague + MFA
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">SKU {product.sku}</span>
            </div>
            <h1 className="font-display text-3xl font-extrabold leading-tight text-navy-deep sm:text-4xl lg:text-5xl">
              {isTh ? `Apostille / MFA — ${name}` : `Apostille / MFA — ${name}`}
            </h1>
            <p data-speakable className="direct-answer mt-4 rounded-xl border-l-4 border-blue-400 bg-blue-50/60 p-4 text-[15px] leading-relaxed text-slate-700">
              {L.directAnswer}
            </p>

            <div className="price-band mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">{L.priceLabel}</div>
                <div className="mt-1 font-display text-2xl font-extrabold text-emerald-800">
                  THB {product.priceMin.toLocaleString()}–{product.priceMax.toLocaleString()}
                </div>
                <div className="mt-0.5 text-xs text-slate-600">{isTh ? "รวมค่าธรรมเนียม MFA มาตรฐาน" : "Includes standard MFA courier fee"}</div>
              </div>
              <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-blue-700">{L.turnLabel}</div>
                <div className="mt-1 flex items-center gap-2 font-display text-2xl font-extrabold text-blue-800">
                  <Clock className="h-5 w-5" /> {product.turnaround}
                </div>
                <div className="mt-0.5 text-xs text-slate-600">{isTh ? `เร่งด่วน ${APOSTILLE_TURNAROUND_EXPRESS} (มีค่าธรรมเนียม)` : `Express ${APOSTILLE_TURNAROUND_EXPRESS} (surcharge)`}</div>
              </div>
            </div>

            <div className="mt-5">
              <ApostilleProductCTA product={product} locale={locale} variant="inline" />
            </div>
            <div className="mt-3 text-xs text-slate-500">
              <ShieldCheck className="mr-1 inline h-3 w-3 text-emerald-600" />
              {L.trust}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              <Clock className="mr-1 inline h-3 w-3 text-blue-600" />
              {L.validity}
            </div>
          </div>

          <aside className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-blue-50/40 p-6">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-blue-700">
              <Globe2 className="h-4 w-4" /> {isTh ? "ปลายทางที่รองรับ" : "Destinations supported"}
            </div>
            <div className="mt-3 rounded-xl border-2 border-dashed border-blue-300 bg-white p-4">
              <div className="text-center">
                <div className="font-display text-4xl font-extrabold text-blue-800">125+</div>
                <div className="mt-1 text-[11px] uppercase tracking-widest text-slate-600">
                  {isTh ? "ประเทศสมาชิก Hague" : "Hague Convention members"}
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                  {isTh ? "รวมจีน (เข้าร่วม 2023-11) — สหรัฐฯ · UK · EU · ออสเตรเลีย · ญี่ปุ่น · เกาหลี" : "Including China (joined Nov 2023) — US · UK · EU · Australia · Japan · Korea"}
                </div>
              </div>
            </div>
            <ul className="mt-5 space-y-1.5 text-[12px] text-slate-700">
              <li className="flex gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600 mt-0.5" /> {isTh ? "ทนาย Notary + นักแปล MoJ ในทีมเดียว" : "In-house notary + MoJ translator"}</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-blue-600 mt-0.5" /> {isTh ? "MFA courier ตรงถึงกรมการกงสุล" : "Direct MFA Consular Affairs courier"}</li>
              <li className="flex gap-2"><Globe2 className="h-3.5 w-3.5 text-blue-600 mt-0.5" /> {isTh ? "ประสานสถานทูตปลายทางในกรุงเทพฯ" : "Bangkok consulate liaison for non-Hague"}</li>
            </ul>
          </aside>
        </div>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.chainTitle}</h2>
        <ol className="mt-4 grid gap-3 md:grid-cols-4">
          {product.chainSteps.map((s, i) => {
            const Icon = chainIcons[i] || FileCheck2;
            return (
              <li key={s} className="relative rounded-2xl border border-blue-200 bg-gradient-to-br from-white to-blue-50/40 p-4">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-700 font-display text-sm font-bold text-white">{i + 1}</span>
                  <Icon className="h-5 w-5 text-blue-700" />
                </div>
                <div className="mt-2 text-[13px] font-semibold text-slate-800">{s}</div>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.whatIncluded}</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {bullets.map((b) => (
            <li key={b} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <span className="text-sm text-slate-700">{b}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">
          {isTh ? `เกี่ยวกับ Apostille ${name}` : `About Apostille for ${name}`}
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-slate-700">{longDesc}</p>
        {product.doctype.thaiUsage.length > 0 && (
          <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
            {isTh ? `กรณีที่ใช้บ่อย: ${product.doctype.thaiUsage.join(" · ")}` : `Common uses: ${product.doctype.thaiUsage.join(" · ")}`}
          </p>
        )}
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.tiers}</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {[
            { label: L.tierPre, desc: L.tierPreDesc, price: 0, tone: "slate" },
            { label: L.tierStd, desc: L.tierStdDesc, price: product.priceMin, tone: "emerald", badge: isTh ? "แนะนำ" : "Popular" },
            { label: L.tierExp, desc: L.tierExpDesc, price: product.priceMax, tone: "blue" },
          ].map((t) => (
            <div key={t.label} className={`relative rounded-2xl border p-5 ${
              t.tone === "emerald" ? "border-emerald-300 bg-emerald-50/40 ring-2 ring-emerald-200"
              : t.tone === "blue" ? "border-blue-300 bg-blue-50/40"
              : "border-slate-300 bg-slate-50/40"
            }`}>
              {t.badge && (
                <span className="absolute -top-2 right-4 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                  {t.badge}
                </span>
              )}
              <div className="font-display text-lg font-extrabold text-navy-deep">{t.label}</div>
              <div className="mt-1 text-xs text-slate-600">{t.desc}</div>
              <div className="mt-3 font-display text-2xl font-extrabold text-navy-deep">
                {t.price === 0 ? (isTh ? "ฟรี" : "Free") : `THB ${t.price.toLocaleString()}`}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.acceptedBy}</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {accepted.map((a) => (
            <span key={a} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
              ✓ {a}
            </span>
          ))}
        </div>
        {product.doctype.thaiUsage.length > 0 && (
          <>
            <h3 className="mt-6 font-display text-lg font-bold text-navy-deep">{L.usedFor}</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {product.doctype.thaiUsage.map((u) => (
                <span key={u} className="rounded-md bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-800 ring-1 ring-blue-200">
                  {u}
                </span>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.howTitle}</h2>
        <ol className="mt-4 grid gap-3 md:grid-cols-2">
          {steps.map((s, i) => (
            <li key={s} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-700 font-display font-bold text-white">
                {i + 1}
              </span>
              <span className="text-sm text-slate-700">{s}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.faqTitle}</h2>
        <div className="mt-4 space-y-3">
          {faqs.map((f) => (
            <details key={f.q} className="speakable-faq group rounded-xl border border-slate-200 bg-white p-4 open:bg-blue-50/30">
              <summary className="cursor-pointer list-none font-display font-bold text-navy-deep">
                <span className="mr-2 text-blue-600 group-open:hidden">+</span>
                <span className="mr-2 hidden text-blue-600 group-open:inline">−</span>
                {f.q}
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <CrossSellStrip source="apostille" locale={locale} />

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.relatedTitle}</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((r) => (
            <Link
              key={r.sku}
              to={isTh ? "/apostille/products/$slug" : "/en/apostille/products/$slug"}
              params={{ slug: r.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-md"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700">
                {isTh ? r.categoryLabelTh : r.categoryLabelEn}
              </div>
              <div className="mt-1 font-display font-extrabold text-navy-deep group-hover:text-blue-700">
                {isTh ? r.nameTh : r.nameEn}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                THB {r.priceMin.toLocaleString()}–{r.priceMax.toLocaleString()} · {r.turnaround}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-blue-700">
                {isTh ? "ดูรายละเอียด" : "View details"} <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <ApostilleProductCTA product={product} locale={locale} variant="sticky-mobile" />
      <ProductDepthBlock cluster="apostille" locale={locale === "th" ? "th" : "en"} />
      <ConsultantAdvisoryBlock locale={locale === "th" ? "th" : "en"} topic="Apostille" />
    </main>
  );
}
